import { jsx as _jsx } from "react/jsx-runtime";
import { Renderer } from '@skand/three-js-renderer';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Vector2 } from 'three';
import { Draw2D, LabelManager, MeshBatch, MeshInstance } from './CoreModules';
import { ClippingService, DeprojectService, DrawService, GlobeService, ModelService, NavigationService, PanoramaService, TransformService, UtilityService, } from './Services';
import { Canvas2D, RenderTarget } from './ViewerStyles';
/**
 * Setup the 3D renderer.
 *
 * @param container
 * @param cesiumToken
 * @param googleToken
 * @returns
 */
async function createRenderer(container, cesiumToken, googleToken) {
    const renderer = await Renderer.create({
        container,
        cesium: {
            token: cesiumToken,
        },
        google: {
            token: googleToken,
        },
        draco: {
            moduleBaseUrl: 'https://www.gstatic.com/draco/versioned/decoders/1.5.0/',
        },
        hb: {
            moduleUrl: `${window.location.origin}/hb.wasm`,
        },
        ifc: {
            moduleBaseUrl: `${window.location.origin}/`,
        },
    });
    return renderer;
}
function ViewerCore({ cesiumToken, googleToken }, ref) {
    const animationRequestRef = React.useRef();
    const frameResolversRef = React.useRef([]);
    const [container3D, setContainer3D] = useState(null);
    const [container2D, setContainer2D] = useState(null);
    const [modules, setModules] = useState(null);
    // Initialize everything
    useEffect(() => {
        if (container3D && container2D && modules === null) {
            createRenderer(container3D, cesiumToken, googleToken).then((renderer) => {
                const draw2D = new Draw2D(container2D);
                const meshBatch = new MeshBatch(renderer);
                const meshInstance = new MeshInstance(renderer);
                const labelManager = new LabelManager(renderer, draw2D);
                // Set modules object
                setModules({
                    renderer,
                    draw2D,
                    meshBatch,
                    meshInstance,
                    labelManager,
                });
                // Resize the 3D and 2D canvases whenever the container resizes
                const resizeObserver = new ResizeObserver(() => {
                    const { offsetWidth, offsetHeight } = container3D;
                    const size = new Vector2(offsetWidth, offsetHeight);
                    renderer.setSize(size);
                    draw2D.setSize(size);
                });
                resizeObserver.observe(container3D);
            });
        }
        // Destroy the renderer on unmount
        return () => {
            if (modules && animationRequestRef.current) {
                modules.renderer.dispose();
                cancelAnimationFrame(animationRequestRef.current);
            }
        };
    }, [modules, cesiumToken, container3D, container2D, googleToken]);
    // Expose API object via ref
    useImperativeHandle(ref, () => {
        if (modules === null)
            return null;
        const utility = new UtilityService(modules, frameResolversRef.current);
        const navigation = new NavigationService(modules);
        const globe = new GlobeService(modules, navigation);
        const draw = new DrawService(modules, navigation);
        const model = new ModelService(modules, globe);
        const transform = new TransformService(modules, navigation);
        const clipping = new ClippingService(modules, navigation);
        const panorama = new PanoramaService(modules, navigation);
        const deproject = new DeprojectService(modules, navigation);
        const api = {
            utility,
            navigation,
            globe,
            draw,
            model,
            transform,
            clipping,
            panorama,
            deproject,
        };
        // Run the main loop
        let lastTime = performance.now();
        const mainLoop = (currTime) => {
            const deltaCap = 250;
            const deltaTime = Math.min(Math.max(currTime - lastTime, 0), deltaCap);
            lastTime = currTime;
            // Service per-frame updates
            const allServices = Object.values(api);
            for (const service of allServices) {
                service.updateFrame(deltaTime / 1000);
            }
            // Core module per-frame updates
            modules.renderer.render();
            modules.draw2D.render();
            modules.meshInstance.update();
            modules.labelManager.update();
            // Invoke next-frame resolvers
            for (const resolver of frameResolversRef.current) {
                resolver();
            }
            frameResolversRef.current.length = 0;
            animationRequestRef.current = window.requestAnimationFrame(mainLoop);
        };
        animationRequestRef.current = window.requestAnimationFrame(mainLoop);
        return api;
    }, [modules]);
    return (_jsx(RenderTarget, { ref: setContainer3D, children: _jsx(Canvas2D, { ref: setContainer2D }) }));
}
/**
 * 3D Viewer Component.
 */
export const Viewer = forwardRef(ViewerCore);
